import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { ThousandSuffixesPipe } from 'src/app/pipe/thousand.pipe';
import { TemporalityWidths } from '../../chartmap-mosaic-shared';

// One row of the score/sentiment events mosaic: magnitude bar + delta pill
// (+ sparkline/temporality when sole). Presentational; callers normalise the
// value/prev/format for both the score and sentiment shapes.
@Component({
  selector: 'app-event-row',
  templateUrl: './event-row.component.html',
  styleUrls: ['./event-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventRowComponent {
  @Input() name = '';
  @Input() value = 0;
  @Input() prevValue: number | null | undefined = null;
  @Input() impact = 1; // +1 good, -1 bad
  @Input() valueFormat: 'count' | 'percent' = 'count';
  @Input() maxValue = 0;

  @Input() series: [number, number][] | undefined;
  @Input() microTimeframe = '';
  @Input() showSparkline = false;
  @Input() showTemporality = false;
  @Input() temporalityWidths: TemporalityWidths | null = null;

  @Input() @HostBinding('class.sole') sole = false;
  @Input() @HostBinding('class.mobile') mobile = false;
  @Input() @HostBinding('class.active') active = false;
  @Input() @HostBinding('id') elementId: string | null = null;

  @Output() rowClick = new EventEmitter<void>();

  @HostListener('click')
  onClick(): void {
    this.rowClick.emit();
  }

  private get hasPrev(): boolean {
    return this.prevValue !== null && this.prevValue !== undefined;
  }

  get barWidth(): number {
    if (!this.maxValue || this.maxValue <= 0) return 0;
    return Math.min(100, (this.value / this.maxValue) * 100);
  }

  get direction(): 'up' | 'down' | 'flat' {
    if (!this.hasPrev) return 'flat';
    const prev = this.prevValue as number;
    if (this.value > prev) return 'up';
    if (this.value < prev) return 'down';
    return 'flat';
  }

  get deltaArrow(): string {
    if (this.direction === 'up') return '↑';
    if (this.direction === 'down') return '↓';
    return '';
  }

  // Polarity-aware: good when the move helps (rise in +1, or fall in -1).
  get deltaClass(): 'positive' | 'negative' | 'neutral' {
    if (this.direction === 'flat') return 'neutral';
    const imp = this.impact ?? 1;
    const good =
      (imp === 1 && this.direction === 'up') ||
      (imp === -1 && this.direction === 'down');
    return good ? 'positive' : 'negative';
  }

  private thousand = new ThousandSuffixesPipe();

  // Absolute change (a relative ratio explodes for tiny baselines): count delta,
  // or percentage-point delta shown as %. `NEW` when there's no prior point.
  get deltaLabel(): string {
    if (!this.hasPrev) return this.value > 0 ? 'NEW' : '';
    const diff = Math.abs(this.value - (this.prevValue as number));
    if (this.valueFormat === 'percent') {
      const points = diff * 100;
      if (points === 0) return '0%';
      return (
        (points < 1 ? points.toFixed(1) : String(Math.round(points))) + '%'
      );
    }
    return String(this.thousand.transform(diff));
  }
}
